import {
  BENEFIT_LONG_LINE_LENGTH,
  benefitIcon,
  parseBenefits,
} from "@/domains/company/company/utils/benefits";

export type BenefitChip = { label: string; icon: string };

export type BenefitsView =
  | { kind: "chips"; items: BenefitChip[] }
  | { kind: "paragraph"; text: string }
  | null;

/** A benefit is a label. Labels do not end in a full stop; sentences do. */
const ENDS_LIKE_A_SENTENCE = /[.!?]$/;

/**
 * Which shape the Benefit section takes.
 *
 * The parsing is not decided here — `parseBenefits` owns it, newline convention
 * and legacy fallback included, and is shared with the settings page so the
 * chips previewed while editing are the chips published. What is decided here
 * is the **form**, which the parser deliberately does not know: it hands back a
 * flat `string[]` and cannot tell three benefits apart from one sentence that
 * happened to contain commas.
 *
 * Two signals decide it (EC4), because one is not enough:
 *
 * 1. **Length.** Any piece past `BENEFIT_LONG_LINE_LENGTH` is prose. Measured on
 *    the *longest* piece, not the average: one runaway line among five short
 *    ones still breaks the row, and the mixed rendering it would take to save
 *    the other four is not worth the seam.
 * 2. **Sentence punctuation, and only where the convention was not used.** The
 *    length rule alone lets "Lavoriamo da remoto, ovunque tu sia, senza obbligo
 *    di sede." through as three chips — every fragment is short. What gives it
 *    away is the full stop. This signal applies **only to text without a
 *    newline**, which is exactly the legacy territory where the ambiguity
 *    lives: once the author used the convention we take their word for the
 *    list, trailing period or not.
 *
 * Chips are worth this trouble: prose set in pills is a paragraph cut into
 * boxes, and at 375px it is unreadable.
 */
export const benefitsView = (raw: string | null | undefined): BenefitsView => {
  const items = parseBenefits(raw);
  if (items.length === 0) return null;

  // The original text, not the re-joined pieces: whatever the fallback split on
  // was punctuation the author wrote, and putting it back is the closest thing
  // to what they meant.
  const text = (raw ?? "").trim();

  const longest = items.reduce((max, item) => Math.max(max, item.length), 0);
  if (longest > BENEFIT_LONG_LINE_LENGTH) return { kind: "paragraph", text };

  const usedTheConvention = /\r?\n/.test(text);
  if (!usedTheConvention && ENDS_LIKE_A_SENTENCE.test(text))
    return { kind: "paragraph", text };

  return {
    kind: "chips",
    items: items.map((label) => ({ label, icon: benefitIcon(label) })),
  };
};
